
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { IFilter, ISorter } from "@/models/tables";
import { DataModules } from "@/store/modules/data";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import AdminBrandId from "@/mixins/adminBrandId";
import { IDomain } from "@/models/domains";
import { CreateElement } from "vue";
import {
  IDomainsProvider,
  IDomainsProviderMethods
} from "@/models/providers/domains";
import { DefaultDomainsFilters } from "@/data/filters/domains";
import { DefaultDomainSorters } from "@/data/sorters/domains";

@Component
export default class DomainsProvider extends Mixins(
  FilteringSortingTableStore,
  AdminBrandId
) {
  // Props

  @Prop({ default: `DP` /** Domains Provider */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultDomainsFilters })
  public availableFilters!: () => IFilter[];

  @Prop({ default: () => DefaultDomainSorters })
  public availableSorters!: () => ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  // Data

  public dataModule = DataModules.OAUTH_CLIENTS;
  public filters: IFilter[] = this.availableFilters();
  public isLoading = false;
  public isReloading = false;
  public sorters: ISorter[] = this.availableSorters();

  // Life-cycle methods

  public created() {
    if (!this.canList) return;
    this.loadData();
  }

  // Computed

  public get canList() {
    return this.$userCan("list_oauth_clients");
  }

  // Methods

  public async loadData() {
    try {
      this.isLoading = true;
      await this.getData();
    } finally {
      this.isLoading = false;
    }
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      this.cancelRequests();
      return this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  public async deleteDomain(domainId: IDomain["id"]) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.domain") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(domainId);
            confirm.close();
          }
        }
      }
    });
  }

  public async onDeleteConfirmed(domainId: IDomain["id"]) {
    try {
      this.table.loading = true;
      await this.$store.dispatch(`data/${DataModules.OAUTH_CLIENTS}/remove`, {
        id: domainId,
        vm: this
      });
      this.getData({ ignoreStored: true });
      this.$buefy.toast.open({
        message: this.$t("_.custom_domain_removed") as string
      });
    } catch (error) {
      this.handleUpdateError(error);
    }
  }

  public verifyDomain(domainId: IDomain["id"]) {
    this.table.loading = true;
    return this.$store
      .dispatch(`data/${DataModules.OAUTH_CLIENTS}/verify`, {
        id: domainId,
        vm: this
      })
      .then(this.handleUpdateSuccess)
      .catch(this.handleUpdateError);
  }

  public handleUpdateSuccess(removed = false) {
    this.getData({ ignoreStored: true });

    this.$buefy.toast.open({
      message: this.$t(
        `_.object_successfully_${!removed ? "removed" : "updated"}`,
        {
          object: this.$t("_.domain") as string
        }
      ) as string
    });
  }

  public handleUpdateError(error) {
    this.$store.dispatch("api/handleValidationError", {
      error,
      vm: this
    });
    this.table.loading = false;
  }

  public update(domainId: IDomain["id"], data: any) {
    this.table.loading = true;
    return this.$store
      .dispatch(`data/${DataModules.OAUTH_CLIENTS}/update`, {
        id: domainId,
        vm: this,
        data
      })
      .then(this.handleUpdateSuccess)
      .catch(this.handleUpdateError);
  }

  private async openManageModal(domainId?: IDomain["id"]) {
    return this.$store.dispatch("data/oauthClients/openManageDomainModal", {
      props: { domainId },
      events: {
        "domain-saved": () => this.getData({ ignoreStored: true })
      }
    });
  }

  public async openUpdateModal(domainId: IDomain["id"]) {
    if (!this.$userCan("update_oauth_client")) return;
    return this.openManageModal(domainId);
  }

  public async openCreateModal() {
    if (!this.$userCan("create_oauth_client")) return;
    return this.openManageModal();
  }

  // Provide

  @Provide("domainsProviderMethods")
  public get providerMethods(): IDomainsProviderMethods {
    return {
      deleteDomain: this.deleteDomain,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openCreateModal: this.openCreateModal,
      openUpdateModal: this.openUpdateModal,
      reloadData: this.reloadData,
      update: this.update,
      verifyDomain: this.verifyDomain
    };
  }

  @Provide("domainsProvider")
  public providerData(): IDomainsProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      constQueryFilters: this.constQueryFilters,
      domains: this.table.data as IDomain[],
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasDomains: !!this.table.data.length,
      hasRequestError: this.requestError,
      isLoading: this.table.loading || this.isLoading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      table: this.table,
      total: this.table.total
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.loadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $domainsData: this.providerData(),
        $domainsMethods: this.providerMethods
      })
    );
  }
}
